import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import { forgetViews } from '@/router/viewMemory'
import { UserRole } from '@/types'
import { useRouter } from 'vue-router'
import { canCheck, type Feature } from '@/config/permissions'
import { demoPermissions, demoWriteGroups, resolveDemoRole, setDemoRole } from '@/config/demoRole'

const BYPASS_MODE = import.meta.env.VITE_AUTH_BYPASS === 'true'
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

function isJwtExpired(token: string | null): boolean {
  if (!token) return true
  try {
    const parts = token.split('.')
    if (parts.length < 2) return true
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const decodedJson = atob(payloadBase64)
    const payload = JSON.parse(decodedJson)
    if (payload && typeof payload.exp === 'number') {
      return Date.now() >= payload.exp * 1000
    }
  } catch (e) {
    console.warn('Failed to parse JWT payload exp:', e)
  }
  return false
}

export const useAuthStore = defineStore('auth', () => {
  // Google GIS SDK token client
  
  const router = useRouter()  // Add router for redirects
  const access_token = ref<string | null>(null)
  const id_token = ref<string | null>(null)
  const refresh_token = ref<string | null>(null)
  const expiry = ref<string | null>(null)

  // User state
  const user = ref<User | null>(null)
  const loading = ref(false)

  // Permissions state (for custom roles)
  const permissions = ref<string[] | null>(null)
  /** Gruppi di campi scrivibili, per entità. `null` significa "non limitato per
   *  campo", che è il caso di ogni ruolo fisso: un dizionario vuoto vorrebbe
   *  dire l'opposto, cioè che non può scrivere niente. */
  const writeGroups = ref<Record<string, string[]> | null>(null)

  // Singleton refresh promise (prevents concurrent refreshes)
  const tokenRefreshPromise = ref<Promise<string> | null>(null)

  // Computed properties
  const isAuthenticated = computed(() => {
    if (BYPASS_MODE) return true
    if (!access_token.value || !user.value) return false
    if (isJwtExpired(access_token.value)) return false
    if (expiry.value) {
      const expiresAt = new Date(expiry.value)
      if (!isNaN(expiresAt.getTime()) && expiresAt <= new Date()) {
        return false
      }
    }
    return true
  })

  // Actions
  const login = async () => {
    if (BYPASS_MODE) {
      // Mock admin user for bypass mode
      const role = resolveDemoRole()
      user.value = {
        user_id: 'bypass-admin',
        name: 'Demo Admin',
        email: 'demo@smeup.com',
        role: role as UserRole,
        is_disabled: false,
        created_at: new Date().toISOString()
      }
      permissions.value = demoPermissions(role)
      writeGroups.value = demoWriteGroups(role)
      return
    }

    // Use OAuth redirect flow instead of popup
    loading.value = true

    // Build Google OAuth URL with redirect
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const redirectUri = `${window.location.origin}/callback`
    const scope = 'openid profile email'
    const responseType = 'code'
    const state = Math.random().toString(36).substring(2, 15) // Random state for CSRF protection

    // Store state in both sessionStorage and localStorage for verification
    sessionStorage.setItem('oauth_state', state)
    localStorage.setItem('oauth_state', state)
    
    // Check for existing saved redirect in sessionStorage or URL query param
    const urlParams = new URLSearchParams(window.location.search)
    const queryRedirect = urlParams.get('redirect')
    const existingRedirect = sessionStorage.getItem('oauth_redirect') || localStorage.getItem('oauth_redirect')
    
    let returnPath = '/'
    if (existingRedirect && existingRedirect !== '/login' && existingRedirect !== '/') {
      returnPath = existingRedirect
    } else if (queryRedirect && queryRedirect !== '/login') {
      returnPath = queryRedirect
    } else {
      const currentPath = window.location.pathname + window.location.search
      if (currentPath !== '/login') {
        returnPath = currentPath
      }
    }
    sessionStorage.setItem('oauth_redirect', returnPath)
    localStorage.setItem('oauth_redirect', returnPath)

    // Redirect to Google OAuth
    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&response_type=${responseType}&state=${state}`

    window.location.href = oauthUrl
  }

  const sendTokenToBackend = async (token: string) => {
    try {
      console.log('🔍 Sending token to backend...')
      // Call backend /auth/verify endpoint to get user profile
      // Use direct API URL for auth endpoints (not proxied through /api/v1)
      const apiUrl = API_BASE_URL.replace('/api/v1', '/api')
      const response = await fetch(`${apiUrl}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      console.log('📡 Verify response status:', response.status)

      if (!response.ok) {
        throw new Error('Token verification failed')
      }

      const userData = await response.json()
      console.log('✅ User data received:', userData)

      // Store the JWT token and user profile
      if (userData.access_token) {
        setAuthData({
          access_token: userData.access_token,
          expiry: userData.expires_at,
          user: {
            user_id: userData.id,
            name: userData.name,
            email: userData.email,
            role: userData.role,
            is_disabled: userData.is_disabled,
            created_at: userData.created_at,
            last_login_at: userData.last_login_at
          }
        })
        console.log('🔑 JWT token and user stored')
      }

      // Fetch permissions for custom roles
      await fetchPermissions()
      console.log('🔐 Permissions fetched')

      loading.value = false

      // Redirect to dashboard after successful login
      const currentRoute = router.currentRoute.value
      const redirect = currentRoute.query.redirect as string || '/'
      console.log('🚀 Redirecting to:', redirect)
      await router.push(redirect)

    } catch (error) {
      loading.value = false
      console.error('❌ Backend verification failed:', error)
      throw error
    }
  }

  const setAuthData = (data: { access_token: string; expiry?: string; user: User }) => {
    access_token.value = data.access_token
    expiry.value = data.expiry || null
    user.value = data.user
    
    try {
      localStorage.setItem('auth_access_token', data.access_token)
      if (data.expiry) localStorage.setItem('auth_expiry', data.expiry)
      localStorage.setItem('auth_user', JSON.stringify(data.user))
    } catch (e) {
      console.warn('Failed to persist auth data:', e)
    }
  }

  const logout = async () => {
    access_token.value = null
    id_token.value = null
    refresh_token.value = null
    expiry.value = null
    user.value = null
    permissions.value = null
    try {
      localStorage.removeItem('auth_access_token')
      localStorage.removeItem('auth_expiry')
      localStorage.removeItem('auth_user')
      sessionStorage.removeItem('oauth_redirect')
      sessionStorage.removeItem('oauth_state')
    } catch (e) {}
    forgetViews()
    loading.value = false
  }

  const checkAuth = async () => {
    if (BYPASS_MODE) {
      const role = resolveDemoRole()
      user.value = {
        user_id: 'bypass-admin',
        name: 'Demo Admin',
        email: 'demo@smeup.com',
        role: role as UserRole,
        is_disabled: false,
        created_at: new Date().toISOString()
      }
      // Nel bypass fetchPermissions() non viene mai chiamata: senza questa riga
      // la cache resta vuota, e per un ruolo personalizzato canAccess chiude
      // tutto - la guardia del router lo mandava su /unauthorized.
      permissions.value = demoPermissions(role)
      writeGroups.value = demoWriteGroups(role)
      return true
    }

    if (!access_token.value) {
      const savedToken = localStorage.getItem('auth_access_token')
      const savedExpiry = localStorage.getItem('auth_expiry')
      const savedUser = localStorage.getItem('auth_user')
      if (savedToken && savedUser) {
        try {
          access_token.value = savedToken
          expiry.value = savedExpiry
          user.value = JSON.parse(savedUser)
        } catch (e) {
          localStorage.removeItem('auth_access_token')
          localStorage.removeItem('auth_expiry')
          localStorage.removeItem('auth_user')
        }
      }
    }

    // Check if token exists or is expired
    if (!access_token.value || isJwtExpired(access_token.value)) {
      await logout()
      return false
    }

    // Verify token with backend and get user profile
    try {
      const apiUrl = API_BASE_URL.replace('/api/v1', '/api')
      const response = await fetch(`${apiUrl}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${access_token.value}`
        }
      })

      if (!response.ok) {
        await logout()
        return false
      }

      const userData = await response.json()
      if (userData.expires_at) {
        expiry.value = userData.expires_at
        localStorage.setItem('auth_expiry', userData.expires_at)
      }
      user.value = {
        user_id: userData.id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        is_disabled: userData.is_disabled,
        created_at: userData.created_at,
        last_login_at: userData.last_login_at
      }
      localStorage.setItem('auth_user', JSON.stringify(user.value))

      return true
    } catch (error) {
      console.error('Auth check failed:', error)
      return false
    }
  }

  const fetchPermissions = async (): Promise<boolean> => {
    if (BYPASS_MODE) {
      const role = resolveDemoRole()
      permissions.value = demoPermissions(role)
      writeGroups.value = demoWriteGroups(role)
      return true
    }

    // Get valid access token
    const token = await getAccessToken()
    if (!token) {
      permissions.value = null
      writeGroups.value = null
      return false
    }

    try {
      const apiUrl = API_BASE_URL.replace('/api/v1', '/api')
      const response = await fetch(`${apiUrl}/auth/permissions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        permissions.value = null
        writeGroups.value = null
        return false
      }

      const data = await response.json()
      permissions.value = data.permissions || []
      writeGroups.value = data.write_groups ?? null
      return true

    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      permissions.value = null
      writeGroups.value = null
      return false
    }
  }

  const getAccessToken = async () => {
    if (BYPASS_MODE) {
      return 'bypass-token'
    }

    // Hydrate token from localStorage if memory state is empty
    if (!access_token.value) {
      const savedToken = localStorage.getItem('auth_access_token')
      const savedExpiry = localStorage.getItem('auth_expiry')
      const savedUser = localStorage.getItem('auth_user')
      if (savedToken && savedUser) {
        try {
          access_token.value = savedToken
          expiry.value = savedExpiry
          user.value = JSON.parse(savedUser)
        } catch (e) {
          localStorage.removeItem('auth_access_token')
          localStorage.removeItem('auth_expiry')
          localStorage.removeItem('auth_user')
        }
      }
    }

    // Check if token exists and is valid
    if (access_token.value) {
      if (isJwtExpired(access_token.value)) {
        console.warn('⚠️ Access token expired, logging out...')
        await logout()
        return null
      }
      return access_token.value
    }

    // No token available
    return null
  }

  const refreshToken = async (retryCount = 0): Promise<string> => {
    // Singleton pattern: Return existing promise if refresh in progress
    if (tokenRefreshPromise.value) {
      return tokenRefreshPromise.value
    }

    // Start new refresh
    tokenRefreshPromise.value = performRefresh(retryCount)

    try {
      const token = await tokenRefreshPromise.value
      return token
    } finally {
      // Clear promise after completion
      tokenRefreshPromise.value = null
    }
  }

  const performRefresh = async (retryCount: number): Promise<string> => {
    try {
      // Note: Google GIS SDK doesn't provide refresh token in implicit flow
      // In production, you'd call backend /auth/refresh endpoint here
      // For now, we'll simulate refresh by calling login() again

      // TODO: Replace with backend refresh endpoint when implemented
      // const response = await fetch('/api/auth/refresh', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ refresh_token: refresh_token.value })
      // })

      // For now, trigger new OAuth flow (user will need to re-authenticate)
      throw new Error('Refresh token not available - please login again')

    } catch (error: any) {
      // Handle network errors with retry
      if (error.message.includes('network') || error.message.includes('fetch')) {
        if (retryCount < 3) {
          // Exponential backoff: 1s, 2s, 4s
          const backoffDelay = Math.pow(2, retryCount) * 1000
          await new Promise(resolve => setTimeout(resolve, backoffDelay))
          return performRefresh(retryCount + 1)
        }
      }

      // Handle invalid_grant error (6-month expiry)
      if (error.message.includes('invalid_grant') || error.message.includes('Refresh token not available')) {
        // Clear tokens
        access_token.value = null
        id_token.value = null
        refresh_token.value = null
        expiry.value = null
        user.value = null

        // Redirect to login with error message
        const router = useRouter()
        router.push({
          name: 'Login',
          query: { error: 'session_expired', message: 'Your session has expired. Please sign in again.' }
        })

        throw new Error('Session expired - please login again')
      }

      throw error
    }
  }

  // Role checking helpers
  const hasRole = (role: UserRole): boolean => {
    if (!user.value) return false
    if (user.value.role === UserRole.UNAUTHORIZED || (user.value.role as string) === 'unauthorized') return false
    return user.value.role === role || (user.value.role as UserRole) === UserRole.ADMIN
  }

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!user.value) return false
    if (user.value.role === UserRole.UNAUTHORIZED || (user.value.role as string) === 'unauthorized') return false
    return roles.includes(user.value.role as UserRole) || (user.value.role as UserRole) === UserRole.ADMIN
  }

  /**
   * Se il ruolo collegato apre un'area.
   *
   * Unica definizione della regola: la guardia del router passa da qui, e
   * `usePermissions.canAccess()` delega a questa. Erano due implementazioni, e
   * questa non conosceva i ruoli personalizzati: `canCheck` consulta la mappa
   * dei ruoli fissi, e un nome inventato non e' in nessuna voce. La guardia
   * respingeva quindi ogni ruolo personalizzato da ogni rotta con una feature,
   * rendendo irraggiungibile tutto cio' che la pagina Ruoli sa creare.
   */
  const canAccess = (feature: Feature): boolean => {
    if (!user.value) return false
    if (user.value.is_disabled) return false
    const role = user.value.role as string
    if (role === UserRole.UNAUTHORIZED || role === 'unauthorized') return false
    if (role === UserRole.ADMIN) return true

    const isFixedRole = (Object.values(UserRole) as string[]).includes(role)
    if (isFixedRole) return canCheck(feature, role as UserRole)

    // Ruolo personalizzato: decide la cache dei permessi. Senza cache si chiude,
    // che e' la risposta prudente finche' non si sa cosa apre.
    return (permissions.value ?? []).includes(feature)
  }

  const checkDisabled = (): boolean => {
    return !!user.value?.is_disabled
  }

  /** Chiamata dal selettore di ruolo demo (Task 4): applica e persiste il ruolo scelto. */
  const applyDemoRole = (role: string) => {
    if (!BYPASS_MODE) return
    setDemoRole(role)
    if (user.value) user.value.role = role as UserRole
    permissions.value = demoPermissions(role)
    writeGroups.value = demoWriteGroups(role)
  }

  return {
    // State
    user,
    loading,
    access_token,
    id_token,
    refresh_token,
    expiry,
    permissions,
    writeGroups,

    // Computed
    isAuthenticated,

    // Actions
    setAuthData,
    login,
    logout,
    checkAuth,
    getAccessToken,
    hasRole,
    hasAnyRole,
    canAccess,
    checkDisabled,
    fetchPermissions,
    sendTokenToBackend,
    refreshToken,
    applyDemoRole
  }
})
